import { isSameMonth } from "date-fns";
import { atom } from "jotai";

import type { CalendarSettings, DateStatus } from "../types";
import { getStringifiedDate } from "../utils/calendarDateUtils";

/**
 * Atom state includes optional discrete multi-select mode. Intersected explicitly so tooling that
 * typechecks this package (e.g. fork-ts-checker from the staff app) always sees the field even if
 * `CalendarSettings` resolution is stale or narrowed elsewhere.
 */
export type CalendarAtomSettings = CalendarSettings & {
  discreteMultiSelect?: boolean;
};

/**
 * this allows us to pass all the needed information directly from the DS component down to the
 * WeekDay component without prop drilling
 */
export const calendarSettingsAtom = atom<CalendarAtomSettings>({
  startDate: "",
  endDate: "",
  onDatePress: (_date: string) => {},
  dateInfo: null,
  fillEmptyDays: true,
  singleDatePicker: true,
  discreteMultiSelect: false,
  clubWeekStartsOn: 1,
});

export const getCalendarSettingsForDateAtom = atom(
  (get) => (curDateObj: Date, monthDateObj: Date) => {
    if (curDateObj.toString() === "Invalid Date") {
      const status: DateStatus = "Disabled";
      return {
        showDate: false,
        displayedDate: "",
        status: status,
        onDatePress: () => {},
        isSelected: false,
        selectedDates: [] as string[],
        discreteMultiSelect: false,
      };
    }
    const calendarSettings = get(calendarSettingsAtom);
    const dateInfo = calendarSettings.dateInfo;

    const displayedDate = getStringifiedDate(
      curDateObj,
      calendarSettings.clubWeekStartsOn
    );

    const dayObject = dateInfo?.get(displayedDate);

    const isOutsideDateRange =
      displayedDate > calendarSettings.endDate ||
      displayedDate < calendarSettings.startDate;

    const status: DateStatus = isOutsideDateRange
      ? "Disabled"
      : dayObject?.status ?? "Default";

    const hideDate = !calendarSettings.fillEmptyDays && isOutsideDateRange;
    const isDateInCurrentMonth = isSameMonth(curDateObj, monthDateObj);

    const showDate = isDateInCurrentMonth && !hideDate;

    const onDatePress = calendarSettings.onDatePress;

    const isSelected = !!dayObject?.selected;

    const selectedDates: string[] = [];

    dateInfo?.forEach((day, date) => {
      if (day.selected === true) {
        selectedDates.push(date);
      }
    });

    selectedDates.sort();

    return {
      showDate,
      displayedDate,
      status,
      onDatePress,
      isSelected,
      selectedDates,
      discreteMultiSelect: calendarSettings.discreteMultiSelect ?? false,
    };
  }
);

/**
 * in ordeer to support highlighting dates between a selected date and a date being hovered, we
 * need to track the hovered date
 */
export const hoveredDateAtom = atom<string | null>(null);

/**
 * this ensures that we are not concerned with a date range between a selected date and
 * a hovered date when it is just a singler date picker.
 */
export const getHoveredDateAtom = atom((get) => {
  const calendarSettings = get(calendarSettingsAtom);
  if (calendarSettings.singleDatePicker) return null;
  return get(hoveredDateAtom);
});
